import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Clock3, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, fmtApiError } from "@/lib/api";
import { fechaBA, horaBA, useAhoraBA } from "@/components/ClockBA";
import { btnPrim, btnSec, inputCls } from "@/components/rrhh/ui";

const isoLocal = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function DemoRelojPage() {
  const qc = useQueryClient();
  const ahora = useAhoraBA();
  const [valor, setValor] = useState("");

  const { data: reloj } = useQuery({
    queryKey: ["demo-reloj-estado"],
    queryFn: async () => (await api.get("/demo/reloj")).data,
    refetchInterval: 5000,
  });

  const mutSet = useMutation({
    mutationFn: async (fecha_hora) => api.post("/demo/reloj", { fecha_hora }),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success("Reloj de demo fijado — corre para todo el sistema (kiosco y paneles)");
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const mutClear = useMutation({
    mutationFn: async () => api.delete("/demo/reloj"),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success("Volvió la hora real");
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  const preset = (h, m) => {
    const d = new Date(ahora);
    d.setHours(h, m, 0, 0);
    mutSet.mutate(isoLocal(d));
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="max-w-xl" data-testid="pagina-demo-reloj">
      <div className="rounded-2xl border border-white/10 bg-[#151A21] p-7">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#2DD4BF] text-black">
            <Clock3 size={22} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Reloj de demo</h2>
            <p className="text-sm text-gray-400">Fijá la hora del sistema para toda la demo.</p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-[#0B0F14] p-5 text-center">
          <p className="font-mono-num text-5xl font-semibold tabular-nums text-white" data-testid="reloj-hora-actual">{horaBA(ahora)}</p>
          <p className="mt-1 text-sm text-gray-400">{fechaBA(ahora)}</p>
          <p className={`mt-2 text-xs font-semibold ${reloj?.activo ? "text-amber-300" : "text-[#2DD4BF]"}`} data-testid="reloj-estado">
            {reloj?.activo ? "RELOJ DE DEMO ACTIVO" : "Hora real"}
          </p>
        </div>

        <div className="mt-5">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Fijar fecha y hora</label>
          <div className="flex gap-2">
            <input
              type="datetime-local"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              className={`${inputCls} flex-1`}
              data-testid="reloj-input"
            />
            <button className={btnPrim} disabled={!valor || mutSet.isPending} onClick={() => mutSet.mutate(valor)} data-testid="reloj-fijar">
              Fijar
            </button>
          </div>
          <div className="mt-2.5 flex gap-2">
            <button className={`${btnSec} text-xs`} onClick={() => preset(10, 31)} data-testid="reloj-preset-1031">Ir a 10:31 (ausencias)</button>
            <button className={`${btnSec} text-xs`} onClick={() => preset(19, 31)} data-testid="reloj-preset-1931">Ir a 19:31 (sin salida)</button>
          </div>
        </div>

        {reloj?.activo && (
          <button
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-2.5 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-400/20"
            onClick={() => mutClear.mutate()}
            disabled={mutClear.isPending}
            data-testid="reloj-volver-real"
          >
            <RotateCcw size={15} /> Volver a hora real
          </button>
        )}

        <p className="mt-5 text-xs leading-relaxed text-gray-500">
          Afecta al kiosco y a los paneles: las fichadas se registran con la hora de demo y los procesos
          de las 10:30 (ausencias) y 19:30 (sin salida) corren con este reloj. Solo lo ve el rol RRHH.
        </p>
      </div>
    </motion.div>
  );
}
